import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MdSearch, MdDownload, MdAddBox } from "react-icons/md";
import TablePageLayout from "components/TablePageLayout";
import Pagination from "components/Pagination";
import BulkImportButton from "components/common/BulkImportButton";
import SampleDownloadButton from "components/common/SampleDownloadButton";
import { getPrograms, deleteProgram } from "api/program.api";
import ProgramTable from "components/program/ProgramTable";
import { InputField } from "components/FormFields";
import CustomButton from "components/CustomButton";
import Alert from "components/common/Alert";
import SideDrawerModal from "components/SideDrawerModal";
import ViewProgramDetails from "components/program/ViewProgramDetails";

export default function Programs() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [alert, setAlert] = useState("");
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("admin");

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );
  const [paginationInfo, setPaginationInfo] = useState({
    totalPrograms: 0,
    totalPages: 1,
  });
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    if (userData._id || userData.userId) {
      setUserId(userData._id || userData.userId);
    }
    if (userData.role) {
      setRole(userData.role);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch programs
  const fetchData = useCallback(async () => {
    if (!selectedOrganizer) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      const res = await getPrograms(selectedOrganizer, {
        page,
        limit,
        search: debouncedSearch,
        signal: controller.signal,
      });

      const payload = res?.data || {};
      const list = payload.programs || (Array.isArray(payload) ? payload : []);

      setData(list);
      setPaginationInfo({
        totalPrograms: payload.pagination?.totalEntries || list.length,
        totalPages: payload.pagination?.totalPages || 1,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Failed to fetch programs:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizer, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
    };
    window.addEventListener("companyChanged", handleOrganizerChange);
    return () =>
      window.removeEventListener("companyChanged", handleOrganizerChange);
  }, []);

  const viewButton = (programData) => {
    setSelectedProgram(programData);
    setIsViewOpen(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(data.map((item, idx) => item._id || idx));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id) => {
    await deleteProgram(id, {
      is_deleted: true,
      deleted_by: userId,
    });
    setAlert("Program deleted successfully");
    fetchData();
  };

  const handleExportCSV = () => {
    const exportData =
      selectedRows.length > 0
        ? data.filter((item, idx) => selectedRows.includes(item._id || idx))
        : data;

    if (!exportData.length) return;

    const headers = ["Title", "Program Type", "Date", "Start Time", "Description"];
    const safeStr = (val) => String(val || "").replace(/"/g, '""');

    const rows = exportData.map((p) => [
      `"${safeStr(p.title)}"`,
      `"${safeStr(p.programType_id?.name)}"`,
      `"${safeStr(p.programDate)}"`,
      `"${safeStr(p.startTime)}"`,
      `"${safeStr(p.description)}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `programs_${selectedRows.length > 0 ? "selected" : "all"}_${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <TablePageLayout>
      {role !== "exhibitor" && (
        <div className="grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col">
            <InputField
              label="SEARCH"
              name="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search program..."
              icon={<MdSearch className="text-lg text-gray-400" />}
            />
          </div>

          <div className="flex flex-col items-start justify-start gap-2 sm:col-span-3 sm:flex-row sm:justify-end lg:col-span-3">
            <CustomButton
              label={
                selectedRows.length > 0
                  ? `Export Selected (${selectedRows.length})`
                  : "Export CSV"
              }
              icon={MdDownload}
              iconPos="left"
              variant="outline"
              color="#1A1A1A"
              borderColor="#02a9e0"
              disabled={data.length === 0}
              onClick={handleExportCSV}
            />
{/* 
            <SampleDownloadButton module="programs" />

            <BulkImportButton
              module="programs"
              label="Import Programs"
              onSuccess={fetchData}
              companyId={selectedOrganizer}
            /> */}

            <CustomButton
              label="Add Program"
              icon={MdAddBox}
              iconPos="right"
              variant="solid"
              color="#02a9e0"
              onClick={() => navigate("/admin/program/create")}
            />
          </div>
        </div>
      )}

      {alert && (
        <Alert type="success" message={alert} onClose={() => setAlert("")} />
      )}

      {loading ? (
        <div className="rounded-xl border bg-white p-12 text-center text-sm text-gray-500 shadow-sm">
          Loading programs...
        </div>
      ) : (
        <div className="table-card overflow-hidden">
          <ProgramTable
            data={data}
            startIndex={(page - 1) * limit}
            onRefresh={fetchData}
            onDelete={handleDelete}
            selectedRows={selectedRows}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            viewButton={viewButton}
            fetchData={()=>{setPage(1); fetchData()}}
          />

          <Pagination
            currentPage={page}
            totalPages={paginationInfo.totalPages}
            totalEntries={paginationInfo.totalPrograms}
            limit={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => setLimit(newLimit)}
          />
        </div>
      )}

      <SideDrawerModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={selectedProgram?.title || "Program Details"}
        width="600px"
      >
        <ViewProgramDetails data={selectedProgram} />
      </SideDrawerModal>
    </TablePageLayout>
  );
}